<template>
  <transition name="bg-modal">
    <div
      v-if="visible"
      class="bg-modal-overlay"
      role="presentation"
      @click.self="close"
    >
      <div
        class="bg-login-modal bg-signup-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bg-signup-title"
        @keydown.esc.prevent="close"
      >
        <header class="bg-login-modal__header">
          <div id="bg-signup-title" class="bg-signup-modal__title-wrap">
            <h3>{{ $t('auth.signupTitle') }}</h3>
            <p>{{ $t('auth.signupDesc') }}</p>
          </div>
          <button
            type="button"
            class="bg-login-modal__close"
            :aria-label="$t('common.close')"
            @click="close"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </header>

        <form class="bg-login-modal__body bg-signup-modal__body" @submit.prevent="submit">
          <label class="bg-login-modal__field">
            <span class="bg-signup-modal__label">{{ $t('auth.loginId') }} <span class="bg-signup-modal__req" aria-hidden="true">*</span></span>
            <input
              ref="firstInput"
              v-model.trim="form.loginid"
              type="text"
              maxlength="12"
              autocomplete="username"
              :placeholder="$t('auth.loginIdRulePlaceholder')"
              required
              :disabled="submitting"
            >
          </label>

          <label class="bg-login-modal__field">
            <span class="bg-signup-modal__label">{{ $t('auth.nickname') }} <span class="bg-signup-modal__req" aria-hidden="true">*</span></span>
            <input
              v-model.trim="form.name"
              type="text"
              maxlength="12"
              autocomplete="nickname"
              :placeholder="$t('auth.nicknamePlaceholder')"
              required
              :disabled="submitting"
            >
          </label>

          <label class="bg-login-modal__field bg-login-modal__field--password">
            <span class="bg-signup-modal__label">{{ $t('auth.password') }} <span class="bg-signup-modal__req" aria-hidden="true">*</span></span>
            <input
              v-model="form.loginpwd"
              :type="showPassword ? 'text' : 'password'"
              maxlength="20"
              autocomplete="new-password"
              :placeholder="$t('auth.passwordRulePlaceholder')"
              required
              :disabled="submitting"
            >
            <button
              type="button"
              class="bg-login-modal__toggle-pwd"
              :aria-label="showPassword ? $t('auth.passwordHide') : $t('auth.passwordShow')"
              tabindex="0"
              @click="showPassword = !showPassword"
            >
              <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" fill="none" stroke="currentColor" stroke-width="1.8" />
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M3 3l18 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                <path d="M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2M6.4 6.4C4.2 7.8 2.6 9.9 2 12s3.5 7 10 7c1.8 0 3.4-.4 4.8-1.1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              </svg>
            </button>
          </label>

          <label class="bg-login-modal__field">
            <span class="bg-signup-modal__label">{{ $t('auth.passwordConfirm') }} <span class="bg-signup-modal__req" aria-hidden="true">*</span></span>
            <input
              v-model="form.loginpwdConfirm"
              type="password"
              maxlength="20"
              autocomplete="new-password"
              :placeholder="$t('auth.passwordConfirmPlaceholder')"
              required
              :disabled="submitting"
            >
          </label>

          <div class="bg-signup-modal__phone-block">
            <span class="bg-signup-modal__label">{{ phoneLabels.phone }} <span class="bg-signup-modal__req" aria-hidden="true">*</span></span>
            <div class="bg-signup-phone-number">
              <select
                v-model="form.phoneCode"
                class="bg-signup-phone-code"
                :disabled="submitting || phoneVerification.verified"
                :aria-label="$t('auth.phone')"
              >
                <option
                  v-for="item in phoneCodes"
                  :key="item.value"
                  :value="item.value"
                >{{ item.label }}</option>
              </select>
              <input
                v-model="form.phone"
                class="bg-signup-phone-input"
                type="tel"
                inputmode="numeric"
                maxlength="15"
                :placeholder="phoneLabels.phone"
                :disabled="submitting || phoneVerification.verified"
                @input="onPhoneInput"
              >
            </div>
            <button
              type="button"
              class="bg-signup-phone-btn bg-signup-phone-btn--send"
              :disabled="!canSendCode || phoneVerification.sending || phoneVerification.verified || submitting"
              @click="sendVerificationCode"
            >
              {{ phoneVerification.sent ? phoneLabels.resendCode : phoneLabels.sendCode }}
            </button>
            <div v-if="phoneVerification.sent" class="bg-signup-phone-verify">
              <input
                v-model.trim="phoneVerification.code"
                class="bg-signup-phone-input"
                type="text"
                inputmode="numeric"
                maxlength="6"
                :placeholder="phoneLabels.codePlaceholder"
                :disabled="submitting || phoneVerification.verified"
              >
              <button
                type="button"
                class="bg-signup-phone-btn bg-signup-phone-btn--confirm"
                :disabled="!canVerifyCode || phoneVerification.verified || submitting"
                @click="verifyCode"
              >
                {{ phoneLabels.confirmCode }}
              </button>
            </div>
            <p
              v-if="phoneMessage"
              class="bg-signup-phone-msg"
              :class="phoneMessageType ? `bg-signup-phone-msg--${phoneMessageType}` : ''"
              role="alert"
            >{{ phoneMessage }}</p>
          </div>

          <label class="bg-login-modal__field">
            <span class="bg-signup-modal__label">{{ $t('auth.referralCode') }} <span class="bg-signup-modal__req" aria-hidden="true">*</span></span>
            <input
              v-model.trim="form.pcode"
              type="text"
              maxlength="12"
              :placeholder="$t('auth.referralCodePlaceholder')"
              required
              :disabled="submitting"
            >
          </label>
          <p class="bg-signup-modal__telegram-hint">
            {{ $t('auth.referralHint') }}
            <button type="button" class="bg-signup-modal__telegram-link" @click="openTelegram">
              {{ $t('nav.telegramInquiry') }}
            </button>
            {{ $t('auth.referralHintSuffix') }}
          </p>

          <p v-if="errorMessage" class="bg-login-modal__error" role="alert">{{ errorMessage }}</p>

          <button type="submit" class="bg-login-modal__submit" :disabled="submitting">
            {{ submitting ? $t('auth.signupSubmitting') : $t('auth.signupSubmit') }}
          </button>
        </form>

        <footer class="bg-login-modal__footer">
          <p>{{ $t('auth.hasAccount') }}</p>
          <button type="button" class="bg-login-modal__signup-link" @click="goLogin">
            {{ $t('auth.goLogin') }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { AuthService } from '@/services'
import i18n from '@/utils/i18n'
import {
  getDefaultSignupPhoneCode,
  getSignupPhoneCodes,
  getSignupPhoneLabels,
  buildSignupPhoneNumber,
  normalizePhoneDigits,
  validateSignupForm
} from '@/utils/signup'

export default {
  name: 'BlackGoldSignupModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showPassword: false,
      submitting: false,
      errorMessage: '',
      phoneMessage: '',
      phoneMessageType: '',
      form: {
        loginid: '',
        name: '',
        loginpwd: '',
        loginpwdConfirm: '',
        phoneCode: getDefaultSignupPhoneCode(i18n.locale),
        phone: '',
        pcode: ''
      },
      phoneVerification: {
        sent: false,
        sending: false,
        code: '',
        verified: false,
        verifiedPhoneNumber: ''
      }
    }
  },
  computed: {
    phoneCodes () {
      return getSignupPhoneCodes(this.$i18n.locale)
    },
    defaultPhoneCode () {
      return getDefaultSignupPhoneCode(this.$i18n.locale)
    },
    phoneLabels () {
      return getSignupPhoneLabels()
    },
    canSendCode () {
      return normalizePhoneDigits(this.form.phone).length >= 7
    },
    canVerifyCode () {
      return String(this.phoneVerification.code || '').trim().length === 6
    },
    fullPhoneNumber () {
      return buildSignupPhoneNumber(this.form.phoneCode, this.form.phone)
    }
  },
  watch: {
    visible (open) {
      if (open) {
        this.errorMessage = ''
        this.showPassword = false
        if (!this.phoneVerification.verified) {
          this.form.phoneCode = this.defaultPhoneCode
        }
        this.$nextTick(() => {
          if (this.$refs.firstInput) {
            this.$refs.firstInput.focus()
          }
        })
      } else {
        this.resetForm()
      }
    },
    'form.phoneCode' () {
      this.resetPhoneVerification()
    },
    '$i18n.locale' () {
      if (!this.visible || this.phoneVerification.verified) return
      this.form.phoneCode = this.defaultPhoneCode
    }
  },
  methods: {
    close () {
      if (this.submitting) return
      this.$emit('close')
    },
    goLogin () {
      if (this.submitting) return
      this.$emit('login')
    },
    openTelegram () {
      if (this.submitting) return
      this.$emit('telegram')
    },
    onPhoneInput (event) {
      this.form.phone = normalizePhoneDigits(event.target.value)
      if (this.phoneVerification.verified || this.phoneVerification.sent) {
        this.resetPhoneVerification()
      }
    },
    resetPhoneVerification () {
      this.phoneVerification.sent = false
      this.phoneVerification.sending = false
      this.phoneVerification.code = ''
      this.phoneVerification.verified = false
      this.phoneVerification.verifiedPhoneNumber = ''
      this.phoneMessage = ''
      this.phoneMessageType = ''
    },
    resetForm () {
      this.form = {
        loginid: '',
        name: '',
        loginpwd: '',
        loginpwdConfirm: '',
        phoneCode: this.defaultPhoneCode,
        phone: '',
        pcode: ''
      }
      this.errorMessage = ''
      this.showPassword = false
      this.submitting = false
      this.resetPhoneVerification()
    },
    async sendVerificationCode () {
      if (!this.canSendCode) {
        this.phoneMessage = this.phoneLabels.invalidPhone
        this.phoneMessageType = 'error'
        return
      }
      this.phoneVerification.sending = true
      this.phoneMessage = ''
      this.phoneMessageType = ''
      try {
        const res = await AuthService.sendPhoneOtp(this.fullPhoneNumber)
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          this.phoneVerification.sent = true
          this.phoneVerification.verified = false
          this.phoneVerification.code = ''
          this.phoneVerification.verifiedPhoneNumber = ''
        } else {
          this.phoneVerification.sent = false
          this.phoneMessage = this.phoneLabels.sendFail
          this.phoneMessageType = 'error'
        }
      } catch (err) {
        this.phoneVerification.sent = false
        this.phoneMessage = this.phoneLabels.sendFail
        this.phoneMessageType = 'error'
      } finally {
        this.phoneVerification.sending = false
      }
    },
    async verifyCode () {
      if (!this.phoneVerification.sent) {
        this.phoneMessage = this.phoneLabels.needCode
        this.phoneMessageType = 'error'
        return
      }
      if (!this.canVerifyCode) return
      try {
        const res = await AuthService.checkPhoneOtp(this.fullPhoneNumber, this.phoneVerification.code)
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          this.phoneVerification.verified = true
          this.phoneVerification.verifiedPhoneNumber = this.fullPhoneNumber
          this.phoneMessage = this.phoneLabels.verified
          this.phoneMessageType = 'ok'
        } else {
          this.phoneVerification.verified = false
          this.phoneVerification.verifiedPhoneNumber = ''
          this.phoneMessage = this.phoneLabels.verifyFail
          this.phoneMessageType = 'error'
        }
      } catch (err) {
        this.phoneVerification.verified = false
        this.phoneVerification.verifiedPhoneNumber = ''
        this.phoneMessage = this.phoneLabels.verifyFail
        this.phoneMessageType = 'error'
      }
    },
    submit () {
      if (this.submitting) return
      this.errorMessage = ''
      const payload = {
        ...this.form,
        phoneVerified: this.phoneVerification.verified,
        verifiedPhone: this.phoneVerification.verifiedPhoneNumber
      }
      const validationError = validateSignupForm(payload)
      if (validationError) {
        this.errorMessage = validationError
        return
      }
      this.submitting = true
      this.$emit('submit', payload)
    },
    setSubmitting (value) {
      this.submitting = value
    },
    setError (message) {
      this.errorMessage = message || ''
      this.submitting = false
    },
    finishSuccess () {
      this.submitting = false
      this.resetForm()
    }
  }
}
</script>
